/**
 * Admin blog management page — CRUD for multilingual blog posts.
 *
 * Provides a card-based list of all posts (including drafts) with
 * create, edit, publish/unpublish, and delete actions. A dialog
 * form supports editing translations per language.
 *
 * @module BlogManagementPage
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Divider,
  alpha,
  Grid2 as Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImageIcon from '@mui/icons-material/Image';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { GREEN, DARK_BLUE } from '../theme';
import { PageTransition, FloatingOrb } from '../components/AnimatedComponents';

interface BlogTranslation {
  title: string;
  excerpt: string;
  content: string;
  meta_description: string;
  meta_keywords: string;
}

interface BlogPost {
  id: string;
  slug: string;
  translations: Record<string, BlogTranslation>;
  author: string;
  category: string;
  tags: string[];
  cover_image: string;
  is_published: boolean;
  reading_time: number;
  created_at: string;
  updated_at: string;
}

const SUPPORTED_LANGUAGES = ['nl', 'en', 'de', 'fr', 'es'];

const emptyTranslation = (): BlogTranslation => ({
  title: '',
  excerpt: '',
  content: '',
  meta_description: '',
  meta_keywords: '',
});

const emptyPost = (): Omit<BlogPost, 'id' | 'reading_time' | 'created_at' | 'updated_at'> => ({
  slug: '',
  translations: Object.fromEntries(SUPPORTED_LANGUAGES.map((l) => [l, emptyTranslation()])),
  author: '',
  category: '',
  tags: [],
  cover_image: '',
  is_published: false,
});

export default function BlogManagementPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [form, setForm] = useState(emptyPost());
  const [tagsInput, setTagsInput] = useState('');
  const [langTab, setLangTab] = useState(0);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; postId: string }>({
    open: false,
    postId: '',
  });

  // Image upload state
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/login');
      return;
    }
    fetchPosts();
  }, [user, isAdmin, navigate]);

  const fetchPosts = useCallback(() => {
    api
      .get('/api/blog/', { params: { published_only: false } })
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const openNewPost = () => {
    setEditingPost(null);
    setForm(emptyPost());
    setTagsInput('');
    setLangTab(0);
    setEditorOpen(true);
  };

  const openEditPost = (post: BlogPost) => {
    setEditingPost(post);
    // Ensure all languages have translations
    const translations: Record<string, BlogTranslation> = {};
    for (const lang of SUPPORTED_LANGUAGES) {
      translations[lang] = post.translations[lang] || emptyTranslation();
    }
    setForm({
      slug: post.slug,
      translations,
      author: post.author,
      category: post.category,
      tags: post.tags,
      cover_image: post.cover_image,
      is_published: post.is_published,
    });
    setTagsInput(post.tags.join(', '));
    setLangTab(0);
    setEditorOpen(true);
  };

  const handleTranslationChange = (lang: string, field: keyof BlogTranslation, value: string) => {
    setForm((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [lang]: {
          ...prev.translations[lang],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: tagsInput
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };

      if (editingPost) {
        await api.put(`/api/blog/${editingPost.id}`, payload);
      } else {
        await api.post('/api/blog/', payload);
      }
      setMessage({ type: 'success', text: t('blog.adminSaved') });
      setEditorOpen(false);
      fetchPosts();
    } catch {
      setMessage({ type: 'error', text: t('blog.adminError') });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/blog/${deleteDialog.postId}`);
      setDeleteDialog({ open: false, postId: '' });
      setMessage({ type: 'success', text: t('blog.adminDeleted') });
      fetchPosts();
    } catch {
      setMessage({ type: 'error', text: t('blog.adminError') });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      setMessage({ type: 'error', text: t('blog.adminImageTypeError') });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setMessage({ type: 'error', text: t('blog.adminImageSizeError') });
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/api/blog/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((prev) => ({ ...prev, cover_image: res.data.url }));
      setMessage({ type: 'success', text: t('blog.adminImageUploaded') });
    } catch {
      setMessage({ type: 'error', text: t('blog.adminError') });
    } finally {
      setUploading(false);
    }
    // Reset the input so the same file can be re-selected
    e.target.value = '';
  };

  const currentLang = SUPPORTED_LANGUAGES[langTab];

  if (!user || !isAdmin) return null;

  return (
    <PageTransition>
      <SEO title={t('blog.adminTitle')} noindex />
      <Box
        sx={{
          py: { xs: 3, md: 5 },
          position: 'relative',
          overflow: 'hidden',
          minHeight: '80vh',
          background: `linear-gradient(180deg, ${alpha(DARK_BLUE, 0.02)} 0%, transparent 40%)`,
        }}
      >
        <FloatingOrb size={250} color={DARK_BLUE} top={-60} left={-80} animationDelay="2s" />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
              <Button
                onClick={() => navigate('/admin')}
                startIcon={<ArrowBackIcon />}
                sx={{ borderRadius: 25 }}
              >
                {t('nav.admin')}
              </Button>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ArticleIcon sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${DARK_BLUE}, ${GREEN})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                {t('blog.adminTitle')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Typography color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                {t('blog.adminSubtitle')}
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<AddIcon />}
                onClick={openNewPost}
                sx={{ borderRadius: 25, ml: 'auto' }}
              >
                {t('blog.adminNewPost')}
              </Button>
            </Stack>
          </motion.div>

          {/* Messages */}
          <AnimatePresence>
            {message && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 3 }}>
                  {message.text}
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Post list */}
          {loading ? (
            <Typography color="text.secondary">{t('admin.loading')}</Typography>
          ) : posts.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>{t('blog.noPosts')}</Typography>
          ) : (
            <Stack spacing={2}>
              {posts.map((post, index) => {
                const tr = post.translations['nl'] || post.translations['en'] || { title: post.slug, excerpt: '' };
                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: index * 0.06 }}
                  >
                    <Card
                      sx={{
                        border: `1px solid ${alpha(DARK_BLUE, 0.06)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: `0 8px 30px ${alpha(DARK_BLUE, 0.08)}`,
                          borderColor: alpha(GREEN, 0.15),
                        },
                      }}
                    >
                      <CardContent>
                        <Grid container spacing={2} alignItems="center">
                          <Grid size={{ xs: 12, md: 7 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {tr.title || post.slug}
                              </Typography>
                              <Chip
                                label={post.is_published ? t('blog.adminPublished') : t('blog.adminDraft')}
                                size="small"
                                color={post.is_published ? 'success' : 'default'}
                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                              />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              /{post.slug} • {post.author} • {new Date(post.created_at).toLocaleDateString()}
                            </Typography>
                            {post.category && (
                              <Chip label={post.category} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                            )}
                            {post.tags.slice(0, 3).map((tag) => (
                              <Chip key={tag} label={tag} size="small" sx={{ mr: 0.5, fontSize: '0.7rem' }} />
                            ))}
                            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
                              {SUPPORTED_LANGUAGES.map((lang) => (
                                <Chip
                                  key={lang}
                                  label={lang.toUpperCase()}
                                  size="small"
                                  color={post.translations[lang]?.title ? 'primary' : 'default'}
                                  variant={post.translations[lang]?.title ? 'filled' : 'outlined'}
                                  sx={{ fontSize: '0.65rem', height: 22, minWidth: 36 }}
                                />
                              ))}
                            </Stack>
                          </Grid>
                          <Grid size={{ xs: 12, md: 5 }}>
                            <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                              <Button
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/blog/${post.slug}`)}
                                sx={{ borderRadius: 25 }}
                              >
                                {t('blog.adminPreview')}
                              </Button>
                              <Button
                                size="small"
                                startIcon={<EditIcon />}
                                variant="outlined"
                                onClick={() => openEditPost(post)}
                                sx={{ borderRadius: 25 }}
                              >
                                {t('blog.adminEditPost')}
                              </Button>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setDeleteDialog({ open: true, postId: post.id })}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </Stack>
          )}
        </Container>

        {/* Blog editor dialog */}
        <Dialog
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            {editingPost ? t('blog.adminEditPost') : t('blog.adminNewPost')}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {/* General fields */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('blog.adminSlug')}
                  value={form.slug}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''),
                    }))
                  }
                  helperText={t('blog.adminSlugHelp')}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('blog.adminAuthor')}
                  value={form.author}
                  onChange={(e) => setForm((prev) => ({ ...prev, author: e.target.value }))}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('blog.adminCategory')}
                  value={form.category}
                  onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('blog.adminTags')}
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    {t('blog.adminCoverImage')}
                  </Typography>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={uploading ? undefined : <CloudUploadIcon />}
                    disabled={uploading}
                    sx={{ borderRadius: 3 }}
                  >
                    {uploading ? t('blog.adminUploading') : t('blog.adminUploadImage')}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      hidden
                      onChange={handleImageUpload}
                    />
                  </Button>
                  {form.cover_image && (
                    <Box
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: `1px solid ${alpha(DARK_BLUE, 0.1)}`,
                        position: 'relative',
                      }}
                    >
                      <Box
                        component="img"
                        src={form.cover_image}
                        alt={t('blog.adminCoverPreviewAlt')}
                        sx={{
                          width: '100%',
                          height: 120,
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                      <Chip
                        icon={<ImageIcon />}
                        label={t('blog.adminImageSet')}
                        size="small"
                        color="success"
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                    </Box>
                  )}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_published}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, is_published: e.target.checked }))
                      }
                      color="secondary"
                    />
                  }
                  label={form.is_published ? t('blog.adminPublished') : t('blog.adminDraft')}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Language tabs for content */}
            <Tabs
              value={langTab}
              onChange={(_, v) => setLangTab(v)}
              variant="scrollable"
              sx={{ mb: 2 }}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Tab
                  key={lang}
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{lang.toUpperCase()}</span>
                      {form.translations[lang]?.title && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: GREEN,
                          }}
                        />
                      )}
                    </Stack>
                  }
                />
              ))}
            </Tabs>

            {/* Translation fields for current language */}
            <Stack spacing={2}>
              <TextField
                fullWidth
                label={t('blog.adminTitle_field')}
                value={form.translations[currentLang]?.title || ''}
                onChange={(e) => handleTranslationChange(currentLang, 'title', e.target.value)}
              />
              <TextField
                fullWidth
                label={t('blog.adminExcerpt')}
                value={form.translations[currentLang]?.excerpt || ''}
                onChange={(e) => handleTranslationChange(currentLang, 'excerpt', e.target.value)}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label={t('blog.adminContent')}
                value={form.translations[currentLang]?.content || ''}
                onChange={(e) => handleTranslationChange(currentLang, 'content', e.target.value)}
                multiline
                rows={12}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.9rem',
                  },
                }}
              />
              <TextField
                fullWidth
                label={t('blog.adminMetaDesc')}
                value={form.translations[currentLang]?.meta_description || ''}
                onChange={(e) => handleTranslationChange(currentLang, 'meta_description', e.target.value)}
                helperText={`${(form.translations[currentLang]?.meta_description || '').length}/160`}
                multiline
                rows={2}
              />
              <TextField
                fullWidth
                label={t('blog.adminMetaKeywords')}
                value={form.translations[currentLang]?.meta_keywords || ''}
                onChange={(e) => handleTranslationChange(currentLang, 'meta_keywords', e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setEditorOpen(false)} sx={{ borderRadius: 25 }}>
              {t('admin.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="secondary"
              disabled={saving || !form.slug || !form.author}
              sx={{ borderRadius: 25, px: 4 }}
            >
              {saving ? t('blog.adminSaving') : t('blog.adminSave')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, postId: '' })}
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>{t('blog.adminDeletePost')}</DialogTitle>
          <DialogContent>
            <Typography>{t('blog.adminDeleteConfirm')}</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteDialog({ open: false, postId: '' })} sx={{ borderRadius: 25 }}>
              {t('admin.cancel')}
            </Button>
            <Button onClick={handleDelete} variant="contained" color="error" sx={{ borderRadius: 25 }}>
              {t('blog.adminDeletePost')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageTransition>
  );
}
